import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import riskService from '../services/risk.service';
import taskService from '../services/task.service';
import activityService from '../services/activity.service';
import { Risk } from '../types/risk.types';
import { Task } from '../types/task.types';

const STALE_MS = 30_000;

export function useRiskStats() {
  return useQuery({
    queryKey: ['risk-stats'],
    queryFn: () => riskService.getRiskStats(),
    staleTime: STALE_MS,
  });
}

export function useTaskStats() {
  return useQuery({
    queryKey: ['task-stats'],
    queryFn: () => taskService.getTaskStats(),
    staleTime: STALE_MS,
  });
}

export function useRiskMatrixData() {
  return useQuery({
    queryKey: ['risk-matrix'],
    queryFn: () => riskService.getRiskMatrix(),
    staleTime: STALE_MS,
  });
}

export function useRecentActivities(limit = 10) {
  return useQuery({
    queryKey: ['recent-activities', limit],
    queryFn: () => activityService.getRecentActivities(limit),
    staleTime: STALE_MS,
  });
}

export interface AttentionItem {
  kind: 'task' | 'risk';
  id: string;
  ref: string;
  title: string;
  annotation: string;
  alert: boolean;          // sev-critical annotation (overdue)
  severityScore?: number;  // risk rows only — drives the severity square
  owner: string;
  date: string;            // mono right column, '' when none
}

export function useAttentionData() {
  const tasks = useQuery({
    queryKey: ['attention-tasks'],
    queryFn: () => taskService.getTasks({ dueDateTo: new Date().toISOString().slice(0, 10) }),
    staleTime: STALE_MS,
  });
  const risks = useQuery({
    queryKey: ['attention-risks'],
    queryFn: () => riskService.getRisks(),
    staleTime: STALE_MS,
  });

  const items = useMemo<AttentionItem[]>(() => {
    const today = new Date();
    const out: AttentionItem[] = [];

    for (const t of (tasks.data ?? []) as Task[]) {
      if (t.status === 'COMPLETED' || t.status === 'CANCELLED' || !t.dueDate) continue;
      const days = Math.floor((today.getTime() - new Date(t.dueDate).getTime()) / 86_400_000);
      if (days <= 0) continue;
      out.push({
        kind: 'task', id: t.id, ref: t.reference_number ?? 'TASK',
        title: t.title, annotation: `overdue ${days}d`, alert: true,
        owner: t.assigneeName ?? t.assigneeEmail ?? 'unassigned',
        date: t.dueDate,
      });
    }
    for (const r of (risks.data ?? []) as Risk[]) {
      const score = (r.residual_score ?? (r.residual_likelihood * r.residual_impact));
      if (r.next_review_date && new Date(r.next_review_date) <= today && r.status !== 'CLOSED') {
        out.push({
          kind: 'risk', id: r.id, ref: r.reference_number, title: r.title,
          annotation: 'review due', alert: false, severityScore: score,
          owner: r.owner_full_name ?? r.owner_email ?? '', date: r.next_review_date,
        });
      } else if (score >= 12 && !r.treatment_due_date && r.status !== 'CLOSED') {
        out.push({
          kind: 'risk', id: r.id, ref: r.reference_number, title: r.title,
          annotation: 'no treatment scheduled', alert: false, severityScore: score,
          owner: r.owner_full_name ?? r.owner_email ?? '', date: '',
        });
      }
    }
    return out
      .sort((a, b) => Number(b.alert) - Number(a.alert) || (b.severityScore ?? 0) - (a.severityScore ?? 0))
      .slice(0, 8);
  }, [tasks.data, risks.data]);

  return { items, count: items.length, isPending: tasks.isPending || risks.isPending };
}
